import { useCallback, useEffect, useRef, useState } from "react";

import {
  DEFAULT_BOARD_ZOOM_PERCENTAGE,
  MAXIMUM_BOARD_ZOOM_PERCENTAGE,
  MINIMUM_BOARD_ZOOM_PERCENTAGE,
} from "@/shelf/constants/shelf-geometry";
import {
  clampBoardZoomPercentage,
  resolveViewFittingBox,
  zoomBoardAroundPoint,
} from "@/shelf/utils/board-projection";
import { resolveShelvesBoundingBox } from "@/shelf/utils/shelf-layout";

import type { LibraryShelf } from "@/cloud/types/types";
import type { ShelfBoardView } from "@/shelf/types/types";

/** How far one press of a zoom control moves the board. */
const ZOOM_STEP = 1.25;

/** The size of the window onto the board, which culling and fitting need. */
interface ViewportSize {
  widthPixels: number;
  heightPixels: number;
}

/**
 * Where the board is looking, and the two ways the reader moves it.
 *
 * The board is panned by dragging it and zoomed by its own controls, rather
 * than by the wheel. This board sits inside a page that scrolls, and a wheel
 * that zoomed instead would take the page's own scrolling away from anyone
 * whose pointer happened to be over it; a wheel held with the platform's zoom
 * modifier is the one exception, because that gesture already means zoom.
 *
 * The view is state rather than a store: it describes the window onto the
 * board, not the arrangement, and nothing outside this page has any use for it.
 */
export const useBoardView = (shelves: readonly LibraryShelf[]) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [viewport, setViewport] = useState<ViewportSize>({ heightPixels: 0, widthPixels: 0 });
  const [view, setView] = useState<ShelfBoardView>({
    panOffsetXPixels: 0,
    panOffsetYPixels: 0,
    zoomPercentage: DEFAULT_BOARD_ZOOM_PERCENTAGE,
  });
  // Whether the reader has taken over. A board that re-fitted itself whenever
  // the pull returned would snatch the view back from somebody who had just
  // panned to the rack they were looking for.
  const hasBeenMoved = useRef(false);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }
    const measure = () =>
      setViewport({
        heightPixels: container.clientHeight,
        widthPixels: container.clientWidth,
      });
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  const fit = useCallback(() => {
    const fitted = resolveViewFittingBox({
      box: resolveShelvesBoundingBox(shelves),
      viewportHeightPixels: viewport.heightPixels,
      viewportWidthPixels: viewport.widthPixels,
    });
    if (fitted) {
      setView(fitted);
    }
    return fitted !== null;
  }, [shelves, viewport.heightPixels, viewport.widthPixels]);

  /**
   * The board's origin is a point in space rather than a corner of the screen,
   * so a view that has never been moved shows a rack standing at the origin
   * half off the window. The first thing the board does is look at what is on
   * it — once, and never again after the reader has moved it themselves.
   */
  useEffect(() => {
    if (hasBeenMoved.current) {
      return;
    }
    if (fit()) {
      hasBeenMoved.current = true;
    }
  }, [fit]);

  /**
   * Zooms by a factor about a point on the board.
   *
   * A factor rather than a percentage, because every caller is saying "closer"
   * or "further" relative to wherever the board is now: a control that had to
   * name the percentage it wanted would have to read the current one first, and
   * two of them reading it at once would disagree.
   */
  const zoomAroundBy = ({
    factor,
    xPixels,
    yPixels,
  }: {
    factor: number;
    xPixels: number;
    yPixels: number;
  }) => {
    hasBeenMoved.current = true;
    setView((current) =>
      zoomBoardAroundPoint({
        view: current,
        xPixels,
        yPixels,
        zoomPercentage: current.zoomPercentage * factor,
      }),
    );
  };

  // Anchored to the middle of the window, which is what a reader pressing a
  // zoom control is looking at — there is no pointer to hold still.
  const zoomFromTheMiddleBy = (factor: number) =>
    zoomAroundBy({
      factor,
      xPixels: viewport.widthPixels / 2,
      yPixels: viewport.heightPixels / 2,
    });

  const panBy = ({ xPixels, yPixels }: { xPixels: number; yPixels: number }) => {
    hasBeenMoved.current = true;
    setView((current) => ({
      ...current,
      panOffsetXPixels: current.panOffsetXPixels + xPixels,
      panOffsetYPixels: current.panOffsetYPixels + yPixels,
    }));
  };

  return {
    canZoomIn: view.zoomPercentage < MAXIMUM_BOARD_ZOOM_PERCENTAGE,
    canZoomOut: view.zoomPercentage > MINIMUM_BOARD_ZOOM_PERCENTAGE,
    containerRef,
    fit,
    panBy,
    view,
    viewport,
    zoomAroundBy,
    zoomIn: () => zoomFromTheMiddleBy(ZOOM_STEP),
    zoomOut: () => zoomFromTheMiddleBy(1 / ZOOM_STEP),
    zoomPercentage: Math.round(clampBoardZoomPercentage(view.zoomPercentage)),
  };
};
